/**
 * The stage (ui/DESIGN.md §3–5): the answer first, the map of this thing full width, the trail under the
 * search field, time at the bottom (floating while the map is the last thing on the page, the map's last row
 * while a lens is open under it; a single record's window is its own span), and the lenses revealed in place
 * under the map. Looked at as of a moment, the answer says so and what came later stays on the map as ghosts.
 * Anything that is one thing opens in the peek; the story plays over the map. The URL is the state: q, step (the trail),
 * range / from / to / asOf, lens, kind (the records lens's data model), process (the runs lens's
 * integration) and peek — the query semantics of pages/Data.tsx, so the cloud's scope logic is untouched. Every view
 * switch is a history step (Back undoes it); the scrubber's moment and "more rows" replace the entry.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from '../lib/liveparams.ts';
import { useEnv } from '../lib/envs.tsx';
import { useAuth } from '../lib/auth.tsx';
import { Api, type Tile } from '../lib/api.ts';
import { rememberSearch } from '../lib/hooks.ts';
import { launch } from '../lib/fly.ts';
import { AsOfContext } from '../lib/asof.tsx';
import { Shell } from '../shell/Shell.tsx';
import { Surface, SystemBadge } from '../design/primitives.tsx';
import { SystemsMap } from '../map/Map.tsx';
import type { MapFlow, MapGeometry } from '../map/types.ts';
import { fromStage } from '../map/adapters.ts';
import { simplifyMap } from '../map/simplify.ts';
import { systemName } from '../lib/systems.ts';
import { Peek, PeekLayout, usePeek } from '../peek/Peek.tsx';
import { Story } from '../story/Story.tsx';
import type { Journey } from '../story/engine.ts';
import { answerOf, type Insight } from '../stage/answer.ts';
import { AnswerCard, type Hop } from '../stage/Answer.tsx';
import { LensTabs } from '../stage/LensTabs.tsx';
import { NamedCard } from '../systems/NamedCard.tsx';
import { EntityStrip, EntityWindow } from '../components/EntityLayer.tsx';
import { Trail } from '../stage/Trail.tsx';
import { Scrubber } from '../stage/Scrubber.tsx';
import { StageLens, type StepFilter } from '../stage/Lenses.tsx';
import { SampleCard, loadSample, type FlowSample } from '../stage/Sample.tsx';
import { useStageData } from '../stage/useStageData.ts';
import { loadJourney, type JourneyRef } from '../stage/journey.ts';
import { DEFAULT_RANGE, isLens, parseAsOf, parseSteps, timeParams, trailValues, windowStart, withStep, type LensId, type Range, type Step } from '../stage/scope.ts';
import { eventHistogram, spanLabel, spanStart } from '../stage/track.ts';
import type { ValueStep } from '../stage/lenses/ValuesLens.tsx';
import { LENS_NONE, patchParams, PEEK_VIEW_KEYS, encodeOnly, encodeStory, parseCount, parseOnly, parseStory } from '../lib/viewstate.ts';
import '../stage/stage.css';

const DEFAULT_LIMIT = 300;
/** The lenses' own view parameters: they go with the lens. */
const LENS_VIEW_KEYS = ['failed', 'run', 'only', 'more', 'call', 'vall', 'vcommon', 'limit'];
const NO_LENS_VIEW = Object.fromEntries(LENS_VIEW_KEYS.map((k) => [k, null]));
/** Room under the map for the scrubber, so it never covers the last lane in its resting place. */
const SCRUBBER_BAND = 72;

export function Stage() {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const navigate = useNavigate();
  const { link } = useEnv();
  const [params, setParams] = useSearchParams();
  const { state: navState } = useLocation();
  const q = params.get('q') ?? '';
  const steps = useMemo(() => parseSteps(params.getAll('step')), [params]);
  const from = params.get('from');
  const to = params.get('to');
  const rangeParam = params.get('range');
  const range = from ? 'custom' : rangeParam ?? DEFAULT_RANGE;
  const asOf = parseAsOf(params.get('asOf'));
  const lensParam = params.get('lens');
  const lens: LensId | null = isLens(lensParam) ? lensParam : null;
  // `lens=none`: the lens the stage opens by itself (under a small map), closed by the user — Back must not reopen it
  const autoClosed = lensParam === LENS_NONE;
  const kind = params.get('kind');
  const process = params.get('process');
  const time = useMemo(() => timeParams(range, from, to, asOf), [range, from, to, asOf]);
  // The same window up to now, while looking as of a moment: for the track and the ghosts of what came later
  const nowTime = useMemo(() => (asOf ? timeParams(range, from, to, null) : null), [range, from, to, asOf]);
  const peek = usePeek();

  // The lenses' own view state is in the URL too (written with replace): failed=1, run=<executionId>, only=<cls>:<state>,
  // more=<rows>, call=1 (changes: every document), vall / vcommon (values), limit=all, story
  const failedOnly = params.get('failed') === '1';
  const filter: StepFilter | null = useMemo(() => parseOnly(params.get('only')), [params]);
  const extra = parseCount(params.get('more'));
  const expandedRun = params.get('run');
  const changesAll = params.get('call') === '1';
  const valuesAll = params.get('vall') === '1';
  const valuesCommon = params.get('vcommon') === '1';
  const limit = params.get('limit') === 'all' ? 2000 : DEFAULT_LIMIT;
  const storyRef = useMemo(() => parseStory(params.get('story')), [params]);

  // What is not in the URL: the moment the handle is at while it moves, the story's speed
  const [liveAsOf, setLiveAsOf] = useState<number | null>(asOf);
  useEffect(() => setLiveAsOf(asOf), [asOf]);
  const [hover, setHover] = useState<{ nodes?: string[]; flows?: string[] } | null>(null);
  const [geometry, setGeometry] = useState<MapGeometry | null>(null);
  const [story, setStory] = useState<Journey | null>(null);
  const [speed, setSpeed] = useState(1);
  const [tile, setTile] = useState<string | null>(null);
  // A lens opened by a click scrolls into place; one opened by itself under a small map (`auto`) does not
  const [reveal, setReveal] = useState(0);
  const [auto, setAuto] = useState<LensId | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    setAuto(null);
  }, [q, tid]);

  const shownLens = lens ?? (autoClosed ? null : auto);
  const data = useStageData({ tid, q, steps, time, nowTime, kind, limit, wantHistory: shownLens === 'changes' });
  const single = !!data.single;

  const answer = useMemo(() => (q.trim() && data.preview !== undefined
    ? answerOf({ q, preview: data.preview, overview: data.overview, records: data.records, runs: data.runs, list: data.ownList ?? data.list, history: data.history, reasons: data.reasons, range, asOf: liveAsOf, now })
    : null), [q, data.preview, data.overview, data.records, data.runs, data.ownList, data.list, data.history, data.reasons, range, liveAsOf, now]);

  const runsList = data.runs?.list;
  const stage = useMemo(() => (data.kinds.length ? fromStage(data.mapKinds, data.kinds, data.ownList ?? data.list, runsList ?? [], now) : null),
    [data.mapKinds, data.kinds, data.ownList, data.list, runsList, now]);
  // Looked at as of a moment (the handle moving, the ▶ sweep, a committed as-of): a node whose first document had
  // not come yet is a ghost (the map greys what is newer than `last`, so it gets its first-seen), and one that was
  // there is judged as of then (its newest document then is at most the moment, and freshness counts from it).
  // Data models the window holds up to now but not as of the moment (data.ghosts) stay as ghosts: a lane never vanishes
  const nodes = useMemo(() => {
    if (!stage) return [];
    const ghost = new Set(data.ghosts);
    if (liveAsOf === null) return ghost.size ? stage.nodes.filter((n) => !ghost.has(n.id)) : stage.nodes;
    const at = liveAsOf;
    const first = new Map(data.mapKinds.map((k) => [k.cls, k.first]));
    return stage.nodes.map((n) => {
      if (ghost.has(n.id)) return { ...n, count: 0, last: at + 1, usual: null, detail: 'not yet' };
      const f = first.get(n.id);
      if (f != null && f > at) return { ...n, last: f, usual: null, detail: 'not yet' };
      return { ...n, last: n.last != null ? Math.min(n.last, at) : n.last };
    });
  }, [stage, liveAsOf, data.mapKinds, data.ghosts]);

  // A busy map made readable as on Now (map/simplify.ts): a hub's steps as one node, copies per system as a family. The
  // story plays over the map as the data has it, so it gets the map unsimplified
  const shown = useMemo(() => {
    if (!stage) return { nodes: [], flows: [], links: [], lanes: new Map() };
    if (story) return { nodes, flows: stage.flows, links: stage.links, lanes: new Map() };
    const s = simplifyMap(nodes, stage.flows, new Set(), systemName);
    const ids = new Set(s.nodes.map((n) => n.id));
    return { nodes: s.nodes, flows: s.flows, links: stage.links.filter((l) => ids.has(l.from) && ids.has(l.to)), lanes: s.lanes };
  }, [stage, nodes, story]);

  // The map fits what is left of the screen under the answer and above the scrubber: lanes adapt 52–96 px
  const [vh, setVh] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight));
  const [headerH, setHeaderH] = useState(56);
  const [answerH, setAnswerH] = useState(170);
  const answerBox = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useLayoutEffect(() => {
    const h = document.querySelector('header');
    const a = answerBox.current;
    const ro = new ResizeObserver(() => {
      if (h) setHeaderH(Math.round(h.getBoundingClientRect().height));
      if (a) setAnswerH(Math.round(a.getBoundingClientRect().height));
    });
    if (h) ro.observe(h);
    if (a) ro.observe(a);
    return () => ro.disconnect();
  }, []);
  // With a lens open the map is a strip: the answer, the strip and the lens's header fit one screen
  const mapMax = shownLens && !story ? 200 : Math.max(340, vh - headerH - 16 - answerH - 16 - SCRUBBER_BAND - 16);
  const route = answer?.route;
  const layout = useMemo(() => ({ offsets: stage?.offsets, laneOrder: route, maxHeight: mapMax }), [stage?.offsets, route, mapMax]);

  // A lens revealed by a click scrolls its header to just under the shell once the layout transition is over,
  // so the stage is seen to grow and the reveal never lands below the fold
  const lensBox = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!reveal) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The answer and the map's strip stay in view above the lens: the page goes to the top, it does not scroll them away
    const into = () => window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    let ro: ResizeObserver | null = null;
    const t = window.setTimeout(() => {
      into();
      // The lens's rows come a moment later and make the page taller: follow them, for a short while
      if (lensBox.current) {
        ro = new ResizeObserver(into);
        ro.observe(lensBox.current);
      }
    }, 300);
    const stop = window.setTimeout(() => ro?.disconnect(), 2500);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(stop);
      ro?.disconnect();
    };
  }, [reveal]);

  // The map's own box, for the story's theatre
  const mapWrap = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    mapRef.current = mapWrap.current?.querySelector<HTMLElement>('.stage-map') ?? null;
  });

  // ---- the URL
  // `set` is a history step: opening or closing a lens, a filter or a toggle of it, the story — Back undoes it. `setView`
  // replaces the entry (the entry's state kept) for what is not a view switch: the moment the scrubber settles on, more rows. Everything one handler changes goes in ONE call: setSearchParams(fn) does not chain.
  const set = useCallback((changes: Record<string, string | null>) => setParams((prev) => patchParams(prev, changes)), [setParams]);
  const setView = useCallback((changes: Record<string, string | null>) => setParams((prev) => patchParams(prev, changes), { replace: true, state: navState }), [setParams, navState]);
  const search = (text: string) => {
    if (!text) {
      void navigate(link('/'));
      return;
    }
    rememberSearch(text);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const k of ['step', 'kind', 'lens', 'process', 'peek', 'asOf', 'entity', ...LENS_VIEW_KEYS, ...PEEK_VIEW_KEYS]) next.delete(k);
      next.set('q', text);
      return next;
    });
  };
  const setSteps = (next: Step[]) => setParams((prev) => {
    const p = new URLSearchParams(prev);
    p.delete('step');
    for (const st of next) p.append('step', JSON.stringify(st));
    return p;
  });
  const addStep = (step: Step) => {
    const next = withStep(steps, step);
    if (next === steps) return;
    launch(step.t);
    setSteps(next);
  };
  const removeStep = (i: number) => setSteps(steps.filter((_, n) => n !== i));

  // ---- the lenses
  const openRecords = (cls: string, f: StepFilter | null = null) => {
    setReveal((x) => x + 1);
    set({ ...NO_LENS_VIEW, lens: 'records', kind: cls, only: encodeOnly(f) });
  };
  const openRuns = (p: string | null, failed = false) => {
    setReveal((x) => x + 1);
    set({ ...NO_LENS_VIEW, lens: 'runs', process: p, failed: failed ? '1' : null });
  };
  const openLens = (l: LensId) => {
    if (l === 'records') openRecords(kind ?? data.list?.cls ?? data.kinds[0]?.cls ?? '');
    else if (l === 'runs') openRuns(process);
    else {
      setReveal((x) => x + 1);
      set({ lens: l });
    }
  };
  const closeLens = () => {
    if (lens) set({ ...NO_LENS_VIEW, lens: null, kind: null, process: null });
    else {
      setAuto(null);
      set({ lens: LENS_NONE });
    }
  };
  const onHop = (h: Hop) => {
    if (h.node) openRecords(h.node);
    else if (h.flow) openRuns(h.flow);
  };
  const onInsight = (i: Insight) => {
    if (i.flow) openRuns(i.flow, true);
    else if (i.node) openRecords(i.node, i.step ?? null);
    else if (i.tile) {
      setTile(i.tile);
      set({ ...NO_LENS_VIEW, lens: 'values' });
    }
  };
  // The values lens scrolls to the tile an insight pointed at, once it is there
  useEffect(() => {
    if (!tile || lens !== 'values' || !data.overview) return;
    const el = document.querySelector<HTMLElement>(`[data-tile="${CSS.escape(tile)}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.animate([{ outline: '2px solid var(--accent)', outlineOffset: '6px', borderRadius: '10px' }, { outline: '2px solid transparent', outlineOffset: '6px' }], { duration: 1600, easing: 'ease-out' });
    setTile(null);
  }, [tile, lens, data.overview]);
  const onPick = (step: ValueStep) => addStep(step);
  const onValue = (t: Tile) => openRecords(t.classes[0] ?? t.sources[0]?.cls ?? '');
  // A chip of the trail: the values lens, scrolled to the attribute it came from
  const onTrailStep = (st: Step) => {
    const t = data.overview?.wall.find((x) => x.label.toLowerCase() === st.l.toLowerCase());
    if (t) setTile(t.id);
    set({ lens: 'values' });
  };

  // ---- time
  // A single record's window is its own span (first seen − 10 % … now) until a range pill is chosen, which the URL then says
  const onRange = (r: Range) => set({ range: r === DEFAULT_RANGE && !single ? null : r, from: null, to: null });
  const onAsOf = (t: number | null, commit: boolean) => {
    setLiveAsOf(t);
    // Every committed nudge, drag end and replay step moves the moment in place: Back goes to where the user was, not through the scrub
    if (commit) setView({ asOf: t === null ? null : String(t) });
  };
  const upToNow = data.full ?? data.preview;
  const first = upToNow?.first ?? null;
  const spanned = single && !rangeParam && !from && first !== null;
  const windowFrom = spanned ? spanStart(first, now) : windowStart(range, from, null, now, first);
  const failedAt = useMemo(() => (runsList ?? []).filter((r) => r.failed > 0).map((r) => r.first), [runsList]);
  // Over its own span the track shows the record's own moments: its documents, its steps, its runs
  const ownRow = data.single ? data.ownList?.rows.find((r) => r.key === data.single?.key) ?? null : null;
  const track = useMemo(() => {
    if (!spanned || !ownRow) return null;
    const times = [ownRow.first, ownRow.last, ...ownRow.steps.filter((c): c is number => typeof c === 'number' && c > 0), ...(runsList ?? []).flatMap((r) => [r.first, r.last])];
    return eventHistogram(times, windowFrom, now);
  }, [spanned, ownRow, runsList, windowFrom, now]);
  // A span within a tenth of a fixed range is that range ("25 h" next to "24 h" says nothing): its pill goes active instead
  const FIXED_MS: Partial<Record<Range, number>> = { '24h': 86_400_000, '7d': 7 * 86_400_000, '30d': 30 * 86_400_000 };
  const own = first !== null ? now - first : null;
  const matching = own === null ? null : (Object.entries(FIXED_MS) as [Range, number][]).find(([, ms]) => Math.abs(own - ms) <= ms * 0.1)?.[0] ?? null;
  const scrub = {
    range: spanned ? matching ?? 'span' : range, onRange, start: windowFrom, now, asOf: liveAsOf, onAsOf, failedAt, single, onStory: () => tellStory(),
    span: single && first !== null && !matching ? { label: spanLabel(first, now), onPick: () => set({ range: null, from: null, to: null }) } : null,
    histogram: track?.histogram ?? upToNow?.histogram ?? [], interval: track?.interval ?? upToNow?.histogramInterval ?? null,
  };

  // A map of one or two nodes ends mid-screen: the stage fills it with the records under the map by itself — for a
  // record whose documents keep coming (a meter's hourly readings), what changed with each of them, in time order
  const rhythmic = !!ownRow && ownRow.messages >= 3 && data.ownList?.records === 1;
  useEffect(() => {
    if (lens || auto || autoClosed || !stage || data.loading || stage.nodes.length > 2) return;
    setAuto(rhythmic ? 'changes' : 'records');
  }, [lens, auto, autoClosed, stage, data.loading, rhythmic]);

  // ---- the story
  // The story is `story=1` (the record the stage is about) or `story=<cls>:<key>` (one opened in a peek), written with replace;
  // the journey itself is loaded from the URL, so closing it (or Back to a stage without it) is just the parameter going
  const storyCls = storyRef ? (storyRef.kind === 'single' ? data.single?.cls : storyRef.cls) : undefined;
  const storyKey = storyRef ? (storyRef.kind === 'single' ? data.single?.key : storyRef.key) : undefined;
  const kindsKnown = data.kinds.length > 0;
  useEffect(() => {
    if (!storyRef) {
      setStory(null);
      return;
    }
    if (!storyCls || !storyKey || !kindsKnown) return;
    let live = true;
    loadJourney(tid, time, data.kinds, { kind: 'record', cls: storyCls, key: storyKey }).then((j) => live && setStory(j), () => undefined); // the card did not come: nothing to tell
    return () => {
      live = false;
    };
  }, [!!storyRef, storyCls, storyKey, kindsKnown, tid]); // eslint-disable-line react-hooks/exhaustive-deps
  const openStory = (ref: JourneyRef) => {
    if (ref.kind !== 'record') return;
    set({ story: encodeStory(data.single && data.single.cls === ref.cls && data.single.key === ref.key ? { kind: 'single' } : { kind: 'record', cls: ref.cls, key: ref.key }) });
  };
  const tellStory = () => {
    if (data.single) openStory({ kind: 'record', ...data.single });
  };

  // ---- a real document under the cursor
  const [glyph, setGlyph] = useState<{ flow: MapFlow; at: { x: number; y: number } } | null>(null);
  const [sample, setSample] = useState<{ id: string; value: FlowSample | null | undefined; error?: string } | null>(null);
  const samples = useRef(new Map<string, Promise<FlowSample | null>>());
  const hideTimer = useRef(0);
  useEffect(() => {
    samples.current.clear();
  }, [data.scopeKey]);
  const systemOfCls = useCallback((cls: string) => data.mapKinds.find((k) => k.cls === cls)?.system ?? cls.slice(0, 3).toUpperCase(), [data.mapKinds]);
  const hideSoon = () => {
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setGlyph(null), 320);
  };
  const onGlyphHover = useCallback((flow: MapFlow | null, at: { x: number; y: number } | null) => {
    if (!flow || !at) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setGlyph(null), 320);
      return;
    }
    window.clearTimeout(hideTimer.current);
    setGlyph({ flow, at });
    const id = flow.id;
    if (!samples.current.has(id)) samples.current.set(id, loadSample(tid, runsList ?? [], flow, systemOfCls));
    setSample((s) => (s?.id === id ? s : { id, value: undefined }));
    samples.current.get(id)!.then(
      (v) => setSample((s) => (s?.id === id ? { id, value: v } : s)),
      (e) => setSample((s) => (s?.id === id ? { id, value: null, error: e instanceof Error ? e.message : String(e) } : s)),
    );
  }, [tid, runsList, systemOfCls]);

  // The card goes when the pointer leaves both the document and the card, even if the glyph moved away without saying so
  const cardBox = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!glyph) return;
    const at = glyph.at;
    const onMove = (e: PointerEvent) => {
      const r = cardBox.current?.getBoundingClientRect();
      const inCard = !!r && e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8;
      if (!inCard && Math.hypot(e.clientX - at.x, e.clientY - at.y) > 56) setGlyph(null);
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [glyph]);

  const trail = trailValues(steps);
  const shellStyle = { '--shell-h': `${headerH}px` } as CSSProperties;
  const bandBelowMap = !shownLens && !story;
  const cardTop = glyph ? glyph.at.y > vh * 0.55 : false;

  return (
    <AsOfContext.Provider value={liveAsOf}>
      <Shell search={{ value: q, onSubmit: search, size: 'compact', trail: steps.length ? <Trail steps={steps} onRemove={removeStep} onOpen={onTrailStep} /> : undefined }}>
        <div className="w-full px-6 pt-4 pb-6" style={shellStyle}>
          {!q.trim() ? (
            <p className="m-0 pt-8 text-body text-n8">Search an order, a customer, an invoice, a run or a value to land here.</p>
          ) : (
            <PeekLayout open={peek.stack.length > 0}
              peek={<Peek time={time} onStory={(r) => r.kind === 'record' && void openStory({ kind: 'record', cls: r.cls, key: r.key })} />}>
              <div className="flex flex-col gap-4 min-w-0">
                <div ref={answerBox} className="stage-answer-box">
                  <NamedCard q={q} />
                  <EntityStrip q={q} />
                  <AnswerCard compact={!!shownLens && !story} answer={answer} trail={trail} lens={lens} single={single} problems={data.problems}
                    asOf={liveAsOf} onBackToNow={() => onAsOf(null, true)} onHop={onHop} onHopHover={(h) => setHover(h ? { nodes: h.node ? [h.node] : undefined, flows: h.flow ? [h.flow] : undefined } : null)}
                    onInsight={onInsight} onStory={tellStory} />
                </div>

                <Surface radius="stage" className="relative" style={{ paddingBottom: bandBelowMap ? SCRUBBER_BAND : 0 }}>
                  <div ref={mapWrap}>
                    {stage ? (
                      <SystemsMap nodes={shown.nodes} flows={shown.flows} links={shown.links} laneLabels={shown.lanes} layout={layout} selection={kind} highlight={hover}
                        asOf={liveAsOf} now={liveAsOf ?? now} motion={story ? 'paused' : 'live'} speed={speed} glyphScale={0.8}
                        onNode={(id) => openRecords(id)} onFlow={(f) => openRuns(f.process)} onGlyphHover={onGlyphHover} geometryRef={setGeometry}
                        className="stage-map rounded-stage" />
                    ) : (
                      <MapSkeleton systems={data.preview?.systems.map((s) => s.code) ?? []} empty={!!data.preview && data.preview.total === 0 && !data.loading} />
                    )}
                  </div>
                  {/* With a lens open under the map the scrubber is the map's last row, so the lens's header is never covered */}
                  {shownLens && !story && <Scrubber {...scrub} sticky={false} className="mx-3" />}
                </Surface>

                {shownLens && !story && <LensTabs lens={shownLens} onLens={openLens} />}
                {shownLens && (
                  <div ref={lensBox} className="stage-dim stage-lens">
                    <StageLens lens={shownLens} data={data} steps={steps} kind={kind} process={process} failedOnly={failedOnly} filter={filter}
                      expandedRun={expandedRun} extra={extra} changesAll={changesAll}
                      values={{ all: valuesAll, common: valuesCommon, onChange: (c) => set({ ...(c.all !== undefined && { vall: c.all ? '1' : null }), ...(c.common !== undefined && { vcommon: c.common ? '1' : null }) }) }}
                      loadExecution={(id) => Api.execution(tid, id)} onClose={closeLens}
                      onKind={(cls) => set({ kind: cls, only: null, more: null, run: null })} onProcess={(p) => set({ process: p, run: null, more: null })}
                      onFailedOnly={(v) => set({ failed: v ? '1' : null })} onNarrow={(f) => set({ only: encodeOnly(f), more: null })}
                      onExpandRun={(id) => set({ run: id })} onExtra={(n) => setView({ more: n > 0 ? String(n) : null })} onChangesAll={(v) => set({ call: v ? '1' : null })}
                      onShowAll={() => setView({ limit: 'all' })} onPick={onPick} onValue={onValue}
                      onRecord={(row, list) => peek.open({ kind: 'record', cls: list.cls, key: row.key })}
                      onRun={(executionId) => peek.open({ kind: 'run', executionId })}
                      onMessage={(ref) => peek.open({ kind: 'document', ref })} />
                  </div>
                )}

                {story && stage ? (
                  <Story journey={story} flows={stage.flows} nodes={stage.nodes} geometry={geometry} mapRef={mapRef} speed={speed} onSpeed={setSpeed}
                    onMessage={(r) => peek.open({ kind: 'document', ref: r })} onClose={() => set({ story: null })} />
                ) : !shownLens && (
                  <>
                    <Scrubber {...scrub} className="mx-3" style={{ marginTop: -SCRUBBER_BAND + 16 }} />
                    <LensTabs lens={null} onLens={openLens} />
                  </>
                )}
              </div>
            </PeekLayout>
          )}
        </div>
      </Shell>

      {q.trim() && <EntityWindow q={q} onSearch={search} />}

      {glyph && (
        <div ref={cardBox} className="fixed z-[45]" data-hold-map="" onPointerEnter={() => window.clearTimeout(hideTimer.current)} onPointerLeave={hideSoon}
          style={{ left: Math.max(12, Math.min(glyph.at.x - 340, (typeof window === 'undefined' ? 1440 : window.innerWidth) - 692)), ...(cardTop ? { bottom: vh - glyph.at.y + 14 } : { top: glyph.at.y + 14 }) }}>
          <SampleCard flow={glyph.flow} sample={sample?.id === glyph.flow.id ? sample.value : undefined} error={sample?.id === glyph.flow.id ? sample.error : undefined}
            onMessage={(ref) => {
              setGlyph(null);
              peek.open({ kind: 'document', ref });
            }} />
        </div>
      )}
    </AsOfContext.Provider>
  );
}

/** The map before its data: a lane per system the preview knows, shimmering where the nodes will be. */
function MapSkeleton({ systems, empty }: { systems: string[]; empty: boolean }) {
  if (empty) return <p className="m-0 px-6 py-10 text-body text-n8">Nothing moved in this time.</p>;
  const lanes = systems.length ? systems : ['', '', '', ''];
  return (
    <div className="flex flex-col gap-1 p-2" aria-hidden="true">
      {lanes.map((s, i) => (
        <div key={`${s}${i}`} className="stage-lane-skeleton flex items-center h-20 pl-4 gap-3">
          {s ? <SystemBadge code={s} size={24} /> : <span className="size-6 rounded-full mv-shimmer" />}
          <span className="w-20 h-3 rounded-full mv-shimmer" style={{ animationDelay: `${i * 60}ms` }} />
          <span className="h-12 rounded-card mv-shimmer" style={{ width: 168, marginLeft: 24 + ((i * 160) % 640), animationDelay: `${i * 60 + 40}ms` }} />
        </div>
      ))}
    </div>
  );
}
